import { useQuery } from '@tanstack/react-query';
import { FC, ReactElement, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { CircularProgress } from 'src/components/CircularProgress';
import { Typography } from 'src/components/Typography';
import { useNativeBridge } from 'src/contexts/NativeBridgeContext';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import { FeatureFlag } from 'src/models/platform/_types';
import { User } from 'src/models/user/_types';
import { checkRequiredItems } from 'src/models/user/user';
import { isAdmin, isSupportRole } from 'src/models/user/utils';
import ActivityPage from 'src/pages/Activity/ActivityPage';
import ActivitySinglePage from 'src/pages/Activity/ActivitySinglePage';
import AppAlertsIndexPage from 'src/pages/Admin/AppAlerts/AppAlertsIndexPage';
import NewAppAlertPage from 'src/pages/Admin/AppAlerts/NewAppAlert/NewAppAlertPage';
import AppFrameLayout from 'src/pages/AppFrameLayout';
import AppointmentsPage from 'src/pages/Appointments/AppointmentsPage';
import { AssessmentPreviewPage } from 'src/pages/Assessments/AssessmentPreviewPage';
import BusinessWaiver from 'src/pages/BusinessWaiver';
import FindClientsPage from 'src/pages/Clients/ClientsListPage/FindClientsPage';
import NewClientPage from 'src/pages/Clients/ClientsListPage/NewClient/NewClientPage';
import DialogScrollTest from 'src/pages/DialogScrollTest';
import EmbedPage from 'src/pages/Embed';
import ErrorPage from 'src/pages/Error';
import EventsPage from 'src/pages/Events/EventsPage';
import FranchiseLayout from 'src/pages/FranchiseFees/FranchiseLayout';
import { IndexPage } from 'src/pages/IndexPage';
import NotificationsUnsubscribePage from 'src/pages/Notifications/NotificationsUnsubscribePage';
import NutritionTestPage from 'src/pages/NutritionTestPage';
import PosPage from 'src/pages/Pos/PosPage';
import PosPreviewPage from 'src/pages/PosPreview';
import SMSSetupPage from 'src/pages/SMSSetupPage';
import CalendarPage from 'src/pages/Schedule/Calendar/CalendarPage';
import { LoginPage } from 'src/pages/Session/LoginPage';
import { ResetPasswordPage } from 'src/pages/Session/ResetPasswordPage';
import { SignupPage } from 'src/pages/Session/SignupPage';
import RolesManagementPage from 'src/pages/SiteConfiguration/RolesManagement/RolesManagementPage';
import TVDisplayPage from 'src/pages/TV/TVDisplayPage';
import LeaderboardsRoutes from 'src/routes/Leaderboards/LeaderboardsRoutes';
import { useHQPermissionCheck } from 'src/shared/HQ/useHQPermissionCheck';
import { hasFeatureFlag } from 'src/shared/usePermissionCheck';

import AutomationsPage from '../pages/Automations/AutomationsPage';

import AppWebviewsRoutes from './AppWebviews/AppWebviewsRoutes';
import AssessmentsRoutes from './Assessments/AssessmentsRoutes';
const ThemeRoutes = lazy(() => import('src/pages/Theme/ThemeRoutes'));

const PosConfigRoutes = lazy(() => import('./PosConfig/PosConfigRoutes'));
const GroupPublicWrapper = lazy(() =>
  import('src/pages/Groups/Public/GroupPublicWrapper').then((m) => ({
    default: m.GroupPublicWrapper,
  }))
);
const PublicExercisePage = lazy(() =>
  import('src/pages/Exercises/PublicExercise/PublicExercisePage').then((m) => ({
    default: m.PublicExercisePage,
  }))
);
const AccessDevicePage = lazy(
  () => import('src/pages/AccessDevices/AccessDevicePage')
);
const AdminRoutes = lazy(() => import('./Admin/AdminRoutes'));
const OAuthTestPage = lazy(
  () => import('src/pages/Admin/OAuthTest/OAuthTestPage')
);

const BookingCheckInBlockerEmbed = lazy(
  () => import('src/pages/Booking/BookingCheckInBlockerEmbed')
);
const CheckInPage = lazy(() => import('src/pages/CheckIn/CheckInPage'));
const ClientRoutes = lazy(() => import('./Client/ClientRoutes'));
const ClientsPage = lazy(
  () => import('src/pages/Clients/ClientsListPage/ClientsPage')
);
const ConnectionsFailurePage = lazy(
  () => import('src/pages/Connections/ConnectionsFailurePage')
);
const ConnectionsPage = lazy(
  () => import('src/pages/Connections/ConnectionsPage')
);
const ContractSigningPage = lazy(
  () => import('src/pages/Contract/ContractSigningPage')
);
const ConnectionsSuccessPage = lazy(
  () => import('src/pages/Connections/ConnectionsSuccessPage')
);
const CreatorRoutes = lazy(() => import('./CreatorRoutes'));
const DashboardPage = lazy(() => import('src/pages/Dashboard/DashboardPage'));
const DataLoadsPage = lazy(() => import('src/pages/DataLoads/DataLoadsPage'));
const DeveloperPage = lazy(() => import('src/pages/Developer/DeveloperPage'));
const EmailRoutes = lazy(() => import('./Email/EmailRoutes'));
const ExerciseRoutes = lazy(() => import('./Exercise/ExerciseRoutes'));
const FaqRoutes = lazy(() => import('./Faq/FaqRoutes'));
const GroupRoutes = lazy(() => import('./Group/GroupRoutes'));
const LifecycleRoutes = lazy(() => import('./Lifecycle/LifecycleRoutes'));
const LinksPage = lazy(() => import('src/pages/Links/LinksPage'));
const LiveReportRoutes = lazy(() => import('./Report/LiveReportRoutes'));
const MeRoutes = lazy(() => import('./Me/MeRoutes'));
const FindersRoutes = lazy(() => import('./Finders/FindersRoutes'));
const PublicFindersRoutes = lazy(() => import('./Finders/PublicFindersRoutes'));
const MessagesPage = lazy(() => import('src/pages/Messages/MessagesPage'));
const MobileAppointment = lazy(
  () => import('src/pages/Appointments/MobileAppointment')
);
const MobileActivityPage = lazy(
  () => import('src/pages/Activity/MobileActivityPage')
);
const MobileCheckin = lazy(() => import('src/pages/CheckIn/MobileCheckin'));
const MobileFindFriends = lazy(
  () => import('src/pages/FindFriends/FindFriendsPage')
);
const MobileGroupCalendars = lazy(
  () => import('src/pages/Groups/Public/MobileGroupCalendars')
);
const MobileContentPage = lazy(
  () => import('src/pages/Content/MobileContentPage')
);
const MobileHabit = lazy(() => import('src/pages/Habits/MobileHabit'));
const MobileFindFriendsPage = lazy(
  () => import('src/pages/FindFriends/FindFriendsPage')
);
const MobilePayBalance = lazy(
  () => import('src/pages/Payments/MobilePayBalance')
);
const MobileResourceDetail = lazy(
  () => import('src/pages/Content/MobileResourceDetail')
);
const AppResourceViewerPage = lazy(
  () => import('src/pages/Resources/AppResourceViewerPage')
);
const MobileVideoDetail = lazy(
  () => import('src/pages/Content/MobileVideoDetail')
);
const MyPackagesPage = lazy(() => import('../pages/MyPackages/MyPackagesPage'));
const NewVideosPage = lazy(
  () => import('src/pages/Videos/NewVideos/NewVideosPage')
);
const OnboardingRoutes = lazy(() => import('./Onboarding/OnboardingRoutes'));
const PrintReportPage = lazy(() => import('src/pages/Reports/PrintReportPage'));
const ProductsRoutes = lazy(() => import('./Products/ProductsRoutes'));
const ProfileCompletionPage = lazy(
  () => import('src/pages/ProfileCompletion/ProfileCompletionPage')
);
const ReportExportPage = lazy(
  () => import('src/pages/Reports/ReportExportPage')
);
const ReportPage = lazy(() => import('src/pages/Reports/ReportPage'));
const ReportsPage = lazy(() => import('src/pages/Reports/ReportsPage'));
const PayrollSummaryPage = lazy(
  () => import('src/pages/Reports/PayrollSummary/PayrollSummaryPage')
);
const PayrollStaffDetailPage = lazy(
  () => import('src/pages/Reports/PayrollStaffDetail/PayrollStaffDetailPage')
);
const ResourcesRoutes = lazy(() => import('./Resources/ResourcesRoutes'));
const RoutinesPage = lazy(() => import('src/pages/Routines/RoutinesPage'));
const ScheduleRoutes = lazy(() => import('./Schedule/ScheduleRoutes'));
const SettingsLayout = lazy(() => import('src/pages/Platform/SettingsLayout'));
const SiteConfigurationRoutes = lazy(
  () => import('./SiteConfiguration/SiteConfigurationRoutes')
);
const SitesRoutes = lazy(() => import('./Sites/SitesRoutes'));
const StaffRoutes = lazy(() => import('./Staff/StaffRoutes'));
const StripeRoutes = lazy(() => import('./Stripe/StripeRoutes'));
const SupportPage = lazy(() => import('src/pages/Support/SupportPage'));
const TagsPage = lazy(() => import('src/pages/Tags/TagsPage'));
const TimeClockPage = lazy(
  () => import('src/pages/TimeTracking/TimeClockPage')
);
const TimeTrackingPage = lazy(
  () => import('src/pages/TimeTracking/TimeTrackingPage')
);
const V2EmbedRoutes = lazy(() => import('./V2Embed/V2EmbedRoutes'));
const VideosPage = lazy(() => import('src/pages/Videos/VideosPage'));
const WorkoutPlansPage = lazy(
  () => import('src/pages/WorkoutPlans/WorkoutPlansPage')
);
const BenchmarksPage = lazy(
  () => import('src/pages/Benchmarks/BenchmarksPage')
);
const HealthReportsPage = lazy(
  () => import('src/pages/Health/Reports/HealthReportsPage')
);
const NewHealthReportPage = lazy(
  () => import('src/pages/Health/Reports/NewHealthReport/NewHealthReportPage')
);
const MacroCalculatorSettingsPage = lazy(
  () => import('src/pages/MacroCalculatorSettings/MacroCalculatorSettingsPage')
);
const MacroCalculatorPage = lazy(
  () => import('src/pages/MacroCalculator/MacroCalculatorPage')
);
const PurchaseRoutes = lazy(() => import('./Purchase/PurchaseRoutes'));
const FranchiseFeeFormPage = lazy(
  () => import('src/pages/FranchiseFees/FeeFormPage')
);
const TrainerBookingPage = lazy(
  () => import('src/pages/TrainerBooking/TrainerBookingPage')
);
const LandingPage = lazy(() => import('src/pages/Landing/LandingPage'));
const ReferAFriendPage = lazy(
  () => import('src/pages/ReferAFriend/ReferAFriendPage')
);
const KioskCheckInPage = lazy(
  () => import('src/pages/CheckIn/KioskCheckInPage')
);
const AccountManagerPage = lazy(
  () => import('src/pages/AccountManager/AccountManagerPage')
);

export default function AppRoutes(): ReactElement {
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const { session } = useOptionalSession();
  const waiversRedesignEnabled = session
    ? hasFeatureFlag(session, FeatureFlag.WaiversRedesign)
    : false;

  return (
    <Suspense fallback={<></>}>
      <Routes>
        {/* Non protected & non app frame routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/notifications"
          element={<NotificationsUnsubscribePage />}
        />
        <Route path="/sign-up" element={<SignupPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="connections-success"
          element={<ConnectionsSuccessPage />}
        />
        <Route
          path="connections-failure"
          element={<ConnectionsFailurePage />}
        />
        <Route path="/purchase/*" element={<PurchaseRoutes />} />
        <Route path="/trainer-booking" element={<TrainerBookingPage />} />
        <Route path="/finders/*" element={<FindersRoutes />} />
        <Route path="/public/finders/*" element={<PublicFindersRoutes />} />
        <Route path="/faq/*" element={<FaqRoutes />} />
        <Route path="/landing/:trainerId?" element={<LandingPage />} />
        <Route path="/tv-display" element={<TVDisplayPage />} />
        {/* Public group page - accessible without login, conditionally shows AppFrame for logged-in users */}
        <Route
          path="/groups/:groupId/public/*"
          element={<GroupPublicWrapper />}
        />
        <Route
          path="/exercises/:exerciseId/public"
          element={<PublicExercisePage />}
        />

        <Route element={<ProtectedRoute />}>
          <Route index element={<IndexPage />} />
          {/* Protected routes outside app frame */}
          <Route path="/invite" element={<ReferAFriendPage />} />
          <Route element={<AppFrameLayout />}>
            {import.meta.env.MODE !== 'production' && (
              <Route path="/theme/*" element={<ThemeRoutes />} />
            )}
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/theme/dialog-test" element={<DialogScrollTest />} />
            <Route
              path="/theme/nutrition-test"
              element={<NutritionTestPage />}
            />
            <Route path="/oauth-test" element={<OAuthTestPage />} />

            {/* Nested route files */}
            <Route path="/onboarding/*" element={<OnboardingRoutes />} />
            <Route path="/exercises/*" element={<ExerciseRoutes />} />
            <Route path="/lifecycle/*" element={<LifecycleRoutes />} />
            <Route path="/products/*" element={<ProductsRoutes />} />
            <Route path="/staff/*" element={<StaffRoutes />} />
            <Route path="/me/*" element={<MeRoutes />} />
            <Route path="/groups/*" element={<GroupRoutes />} />
            <Route path="/client/*" element={<ClientRoutes />} />
            <Route path="/fbm/*" element={<ScheduleRoutes />} />
            <Route path="/v2Embed/*" element={<V2EmbedRoutes />} />
            <Route path="/sites/*" element={<SitesRoutes />} />
            <Route path="/automations/*" element={<AutomationsPage />} />

            <Route
              path="/site-configuration/*"
              element={<SiteConfigurationRoutes />}
            />
            <Route
              path="/settings/:tabName/:id?/edit?"
              element={<SettingsLayout />}
            />
            <Route path="/emails/*" element={<EmailRoutes />} />
            <Route path="/stripe/*" element={<StripeRoutes />} />

            {/* Trainer Assessments */}
            <Route
              path="/trainer/assessments/*"
              element={<AssessmentsRoutes />}
            />
            <Route
              path="/trainer/assessment/:assessmentId"
              element={<AssessmentPreviewPage />}
            />

            {/* Clients */}
            <Route path="clients/" element={<ClientsPage />} />
            <Route path="clients/find" element={<FindClientsPage />} />
            <Route path="clients/:clientId/*" element={<ClientRoutes />} />
            <Route path="clients/new" element={<NewClientPage />} />

            {/* Reports */}
            <Route path="/reports/live/*" element={<LiveReportRoutes />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/report/payroll_summary" element={<PayrollSummaryPage />} />
            <Route
              path="/report/payroll_staff_detail/staff/:trainerId"
              element={<PayrollStaffDetailPage />}
            />
            <Route path="/report/:reportType" element={<ReportPage />} />
            <Route
              path="/report/:reportType/print"
              element={<PrintReportPage />}
            />
            <Route
              path="/report/:reportType/export"
              element={<ReportExportPage />}
            />

            {/* Connections */}
            <Route path="connections" element={<ConnectionsPage />} />

            {/* Macro Calculator */}
            <Route path="/macro-calculator" element={<MacroCalculatorPage />} />
            <Route
              path="/macro-calculator-settings"
              element={<MacroCalculatorSettingsPage />}
            />

            {/* Embed */}
            <Route
              path="/embed/"
              element={<Navigate to="/embed/dashboard" replace />}
            />
            <Route path="/embed/*" element={<EmbedPage />} />

            {/* Videos */}
            <Route path="/videos" element={<VideosPage />} />
            <Route path="/videos/:videoId" element={<NewVideosPage />} />

            {/* Leaderboards */}
            <Route path="/leaderboards/*" element={<LeaderboardsRoutes />} />

            {/* Misc pages without significant child pages */}
            <Route path="/support" element={<SupportPage />} />
            <Route path="/dashboard/" element={<DashboardPage />} />
            <Route path="/tags" element={<TagsPage />} />
            <Route path="/resources/*" element={<ResourcesRoutes />} />
            <Route path="/events" element={<EventsPage />} />
            <Route
              path="/messages/:conversationId?/:messageId?"
              element={<MessagesPage />}
            />
            <Route path="/time-tracking" element={<TimeTrackingPage />} />

            <Route path="/data-loads" element={<DataLoadsPage />} />
            <Route path="/pos-config/*" element={<PosConfigRoutes />} />
            <Route path="/roles-management" element={<RolesManagementPage />} />
            <Route path="/benchmarks" element={<BenchmarksPage />} />

            <Route path="workout-plans" element={<WorkoutPlansPage />} />
            <Route path="routines" element={<RoutinesPage />} />
            <Route path="links" element={<LinksPage />} />

            <Route
              path="franchise-fees/:tabName?/:id?"
              element={<FranchiseLayout />}
            />
            <Route
              path="franchise-fees/new"
              element={<FranchiseFeeFormPage />}
            />
            <Route
              path="franchise-fees/:id/edit"
              element={<FranchiseFeeFormPage />}
            />

            <Route path="/health/reports" element={<HealthReportsPage />} />
            <Route
              path="/health/reports/:id"
              element={<NewHealthReportPage />}
            />
            <Route path="/plans/*" element={<CreatorRoutes />} />
            {/* Activity Page */}
            <Route path="/activity" element={<ActivityPage />} />
            <Route
              path="/activity/:activityId"
              element={<ActivitySinglePage />}
            />
            {/* Modal Alerts */}
            {/* Matches v3 URL /a/admin/modal-alerts; React uses /admin/modal-alerts */}
            <Route
              path="/admin/modal-alerts"
              element={<AppAlertsIndexPage />}
            />
            <Route
              path="/admin/modal-alerts/new"
              element={<NewAppAlertPage />}
            />
            <Route
              path="/admin/modal-alerts/:id/edit"
              element={<NewAppAlertPage />}
            />
          </Route>

          {/* Admin route has own app frame */}
          <Route
            path="/pos/:locationId/:configId/:cardReaderId?"
            element={<PosPage posType="pos" />}
          />

          <Route
            path="/pos-preview/:posType?"
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <PosPreviewPage />
              </Suspense>
            }
          />
          <Route path="/admin/*" element={<AdminRoutes />} />

          {/* OUTSIDE APP FRAME */}
          <Route path="/checkin" element={<CheckInPage />} />
          <Route path="/appointments/:filter?" element={<AppointmentsPage />} />
          <Route path="/my-packages/:filter?" element={<MyPackagesPage />} />
          <Route path="/mobile/activity" element={<MobileActivityPage />} />
          <Route path="/mobile/*" element={<AppWebviewsRoutes />} />
          <Route path="/mobile/checkin" element={<MobileCheckin />} />
          <Route path="/mobile/appointment" element={<MobileAppointment />} />
          <Route path="/mobile/find-friends" element={<MobileFindFriends />} />
          <Route
            path="/mobile/group-calendars"
            element={<MobileGroupCalendars />}
          />
          <Route path="/mobile/habit" element={<MobileHabit />} />
          <Route path="/mobile/activity" element={<MobileActivityPage />} />
          <Route path="/mobile/pay-balance" element={<MobilePayBalance />} />
          <Route path="/mobile/content" element={<MobileContentPage />} />
          <Route
            path="/mobile/content/resource/:resourceType/:resourceId"
            element={<MobileResourceDetail />}
          />
          <Route
            path="/mobile/content/video/:videoType/:videoId"
            element={<MobileVideoDetail />}
          />
          <Route
            path="/mobile/find-friends"
            element={<MobileFindFriendsPage />}
          />
          <Route
            path="/app-webviews/resources/*"
            element={<Navigate to="/mobile/content" replace />}
          />
          <Route
            path="/app-webviews/app-resources/:resourceId"
            element={<AppResourceViewerPage />}
          />
          <Route
            path="/app-webviews/videos/*"
            element={<Navigate to="/mobile/content" replace />}
          />
          <Route path="/developers" element={<DeveloperPage />} />
          <Route path="/time-clock" element={<TimeClockPage />} />
          <Route path="/business-waiver" element={<BusinessWaiver />} />
          {waiversRedesignEnabled ? (
            <Route
              path="/contract/:contractId"
              element={<ContractSigningPage />}
            />
          ) : null}
          <Route path="/sms" element={<SMSSetupPage />} />
          <Route
            path="/access-devices/:id?/edit?"
            element={<AccessDevicePage />}
          />
          <Route path="/account-manager" element={<AccountManagerPage />} />
        </Route>

        <Route element={<ProtectedRoute isProfileCompletion={true} />}>
          <Route
            path="/profile-completion"
            element={<ProfileCompletionPage />}
          />
        </Route>
        <Route
          path="/kiosk/:locationId/:configId/:cardReaderId?"
          element={<PosPage posType="kiosk" />}
        />
        <Route path="/check-in/:magicLink" element={<KioskCheckInPage />} />

        <Route element={<ProtectedRoute />}>
          <Route
            path="/booking/check-in-blocker"
            element={<BookingCheckInBlockerEmbed />}
          />
        </Route>

        {/* Error handling/catch routes */}
        <Route
          path="*"
          element={
            <ErrorPage type={404} title={tCommon('errors.pageNotFound')} />
          }
        />
        <Route path="401" element={<UnauthorizedPage />} />
      </Routes>
    </Suspense>
  );
}

interface ProtectedRouteProps {
  adminOnly?: boolean;
  redirectPath?: string;
  children?: ReactElement;
  isProfileCompletion?: boolean;
}

export const ProtectedRoute: FC<ProtectedRouteProps> = ({
  adminOnly = false,
  children,
  isProfileCompletion = false,
}) => {
  const { user, userLoading, sessionLoading, trainerLoading, session } =
    useOptionalSession();
  const { isWebview } = useNativeBridge();
  const location = useLocation();
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const permissions = useHQPermissionCheck();

  const { data: requiredItemsData, isLoading: checkingRequiredItems } =
    useQuery({
      queryKey: ['requiredItems', user?.id],
      queryFn: () => checkRequiredItems(),
      enabled: !!user?.id && !adminOnly && !isWebview,
      retry: false,
      gcTime: 5 * 60 * 1000,
      staleTime: 5 * 60 * 1000,
    });

  if (
    userLoading ||
    sessionLoading ||
    checkingRequiredItems ||
    trainerLoading
  ) {
    return (
      <Box
        component="main"
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          minHeight: '100vh',
          backgroundColor: '#ffffff',
        }}
      >
        <CircularProgress size="large" sx={{ color: '#1976d2' }} />
        <Typography
          align="center"
          sx={{
            color: '#666666',
            mt: 5,
          }}
        >
          {tCommon('labels.loading')}
        </Typography>
      </Box>
    );
  }

  if (!isLoggedIn({ user, adminOnly })) {
    return (
      <Navigate
        to="/login"
        state={{
          unauthorizedMessage: adminOnly
            ? tCommon('errors.adminAccess')
            : tCommon('errors.notLoggedIn'),
          from: location.pathname + location.search,
        }}
        replace
      />
    );
  }

  const authResult = isAuthorized({ location, permissions });
  if (!authResult.authorized) {
    return (
      <Navigate
        to="/401"
        state={{
          customMessage:
            authResult.message || tCommon('errors.insufficientPermissions'),
        }}
        replace
      />
    );
  }

  const waiversRedesignEnabled = session
    ? hasFeatureFlag(session, FeatureFlag.WaiversRedesign)
    : false;
  const normalizedPathname = location.pathname.replace(/^\/ex4/, '');
  const isContractSigningPage =
    waiversRedesignEnabled && normalizedPathname.startsWith('/contract/');

  if (
    requiredItemsData?.redirect &&
    !isProfileCompletion &&
    !isContractSigningPage
  ) {
    const baseUrl = import.meta.env.BASE_URL; // Get base URL from Vite config
    const currentPath =
      `${baseUrl}${location.pathname}${location.search}`.replace('//', '/');
    const currentUrl = `${window.location.protocol}//${window.location.host}${currentPath}`;

    if (typeof requiredItemsData.redirect === 'string') {
      const redirectUrl = new URL(
        requiredItemsData.redirect,
        externalLinks.railsBase
      );

      redirectUrl.searchParams.append('redirect_to', currentUrl);

      window.location.replace(redirectUrl.toString());
      return null;
    } else if (typeof requiredItemsData.redirect === 'object') {
      const { path, params = {}, type } = requiredItemsData.redirect;
      const searchParams = new URLSearchParams({
        ...params,
        redirect_to: currentUrl,
      });
      const search = String(path).split('?')[1] || '';
      const profileCompletionPath =
        '/profile-completion/' + (search ? '?' + search : '');

      if (type === 'waiver') {
        window.location.replace(
          `${externalLinks.base}${profileCompletionPath}${
            String(path).includes('?') ? '&' : '?'
          }${searchParams}`
        );
        return null;
      }

      if (type === 'contract' && waiversRedesignEnabled) {
        const rawPath = String(path || '');
        const normalizedPath = rawPath
          .replace(/^\/a\/?/, '/')
          .replace(/^\/ex4\/?/, '/');

        window.location.replace(
          `${externalLinks.base}${normalizedPath}${
            normalizedPath.includes('?') ? '&' : '?'
          }${searchParams}`
        );
        return null;
      }

      window.location.replace(
        `${externalLinks.railsBase}${path}${
          String(path).includes('?') ? '&' : '?'
        }${searchParams}`
      );
      return null;
    }
  }

  return children ? children : <Outlet />;
};

function isLoggedIn({
  user,
  adminOnly,
}: {
  user: User | undefined | null;
  adminOnly: boolean;
}): boolean {
  if (adminOnly) {
    return !!user && (isAdmin(user) || isSupportRole(user));
  } else {
    if (!user?.id) return false;
    return !adminOnly || user?.admin;
  }
}

function isAuthorized({ location, permissions }): {
  authorized: boolean;
  message?: string;
} {
  if (location.pathname.startsWith('/dashboard')) {
    return {
      authorized: permissions.canViewHQDashboard(),
      message: "You don't have permission to access the dashboard",
    };
  }
  if (
    location.pathname.startsWith('/reports') ||
    location.pathname.startsWith('/report/')
  ) {
    return {
      authorized: permissions.canViewHQReports(),
      message: "You don't have permission to access reports",
    };
  }
  if (location.pathname.startsWith('/data-loads')) {
    return {
      authorized: permissions.canManageHQDataLoads(),
      message: "You don't have permission to access data loads",
    };
  }
  if (location.pathname.startsWith('/staff')) {
    return {
      authorized: permissions.canManageHQStaff(),
      message: "You don't have permission to access staff management",
    };
  }
  if (
    location.pathname.startsWith('/settings/') ||
    location.pathname.startsWith('/roles-management') ||
    location.pathname.startsWith('/site-configuration/') ||
    location.pathname.startsWith('/lifecycle')
  ) {
    return {
      authorized: permissions.canViewHQCorporateSettings(),
      message: "You don't have permission to access corporate settings",
    };
  }
  if (location.pathname.startsWith('/franchise-fees')) {
    return {
      authorized: permissions.canViewHQFranchiseFees(),
      message: "You don't have permission to access franchise fees",
    };
  }
  return { authorized: true };
}

function UnauthorizedPage(): ReactElement {
  const location = useLocation();
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });

  const customMessage = location.state?.customMessage;

  return (
    <ErrorPage
      type={401}
      title={tCommon('errors.notAuthorized')}
      message={customMessage || tCommon('errors.insufficientPermissions')}
    />
  );
}
